/**
 * RTK Query API for Revision System
 *
 * API slice for revision operations with IndexedDB-first architecture.
 * Optional cloud sync endpoints for account users.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  Revision,
  RevisionHistoryFilter,
  RevisionSettings,
  RevisionStats,
  SyncStatus,
  DiffResult,
  RevisionExport,
} from '../types';
import { RevisionManager } from '../RevisionManager';

// Create a singleton revision manager
let revisionManager: RevisionManager | null = null;

const getRevisionManager = () => {
  if (!revisionManager) {
    revisionManager = new RevisionManager();
  }
  return revisionManager;
};

export const revisionApi = createApi({
  reducerPath: 'revisionApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'Revision',
    'RevisionHistory',
    'RevisionStats',
    'RevisionSettings',
  ],
  endpoints: (builder) => ({
    // Create a new revision
    createRevision: builder.mutation<
      Revision,
      {
        fileId: string;
        filePath: string;
        content: string;
        metadata?: Partial<Revision['metadata']>;
      }
    >({
      queryFn: async ({ fileId, filePath, content, metadata }) => {
        try {
          const manager = getRevisionManager();
          const revision = await manager.createRevision(
            fileId,
            filePath,
            content,
            metadata
          );
          return { data: revision };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { fileId }) => [
        { type: 'RevisionHistory', id: fileId },
        'RevisionStats',
      ],
    }),

    // Get revision history
    getRevisionHistory: builder.query<Revision[], RevisionHistoryFilter>({
      queryFn: async (filter) => {
        try {
          const manager = getRevisionManager();
          const history = await manager.getRevisionHistory(filter);
          return { data: history };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      providesTags: (result, _error, filter) =>
        result
          ? [
              { type: 'RevisionHistory', id: filter.fileId || 'LIST' },
              ...result.map((revision) => ({
                type: 'Revision' as const,
                id: revision.id,
              })),
            ]
          : [{ type: 'RevisionHistory', id: filter.fileId || 'LIST' }],
    }),

    // Get specific revision
    getRevision: builder.query<Revision | null, string>({
      queryFn: async (revisionId) => {
        try {
          const manager = getRevisionManager();
          const content = await manager.getRevisionContent(revisionId);
          if (!content) {
            return { data: null };
          }
          // Note: This returns content, not the full revision object
          // You might want to enhance RevisionManager to return full revision
          return { data: { content } as any };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      providesTags: (_result, _error, id) => [{ type: 'Revision', id }],
    }),

    // Generate diff between revisions
    generateDiff: builder.query<
      DiffResult,
      { fromRevisionId: string; toRevisionId: string }
    >({
      queryFn: async ({ fromRevisionId, toRevisionId }) => {
        try {
          const manager = getRevisionManager();
          const diff = await manager.generateDiff(fromRevisionId, toRevisionId);
          return { data: diff };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
    }),

    // Get revision statistics
    getRevisionStats: builder.query<RevisionStats, void>({
      queryFn: async () => {
        try {
          const manager = getRevisionManager();
          const stats = await manager.getStatistics();
          return { data: stats };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      providesTags: ['RevisionStats'],
    }),

    // Get revision settings
    getRevisionSettings: builder.query<RevisionSettings, void>({
      queryFn: async () => {
        try {
          const manager = getRevisionManager();
          const storage = (manager as any).storage;
          const settings = await storage.getSettings();
          return { data: settings };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      providesTags: ['RevisionSettings'],
    }),

    // Update revision settings
    updateRevisionSettings: builder.mutation<void, Partial<RevisionSettings>>({
      queryFn: async (settings) => {
        try {
          const manager = getRevisionManager();
          await manager.updateSettings(settings);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      invalidatesTags: ['RevisionSettings'],
    }),

    // Export revisions
    exportRevisions: builder.query<
      RevisionExport,
      RevisionHistoryFilter | undefined
    >({
      queryFn: async (filter) => {
        try {
          const manager = getRevisionManager();
          const exportData = await manager.exportRevisions(filter);
          return { data: exportData };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
    }),

    // Import revisions
    importRevisions: builder.mutation<void, RevisionExport>({
      queryFn: async (exportData) => {
        try {
          const manager = getRevisionManager();
          await manager.importRevisions(exportData);
          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      invalidatesTags: ['RevisionHistory', 'RevisionStats'],
    }),

    // Cloud sync endpoints (for account users only)
    syncRevisionToCloud: builder.mutation<
      void,
      { revisionId: string; authToken: string }
    >({
      queryFn: async ({ revisionId, authToken }) => {
        try {
          // Get revision from local storage
          const manager = getRevisionManager();
          const storage = (manager as any).storage;
          const revision = await storage.getRevision(revisionId);

          if (!revision) {
            throw new Error('Revision not found');
          }

          // Upload to cloud (only if user is authenticated)
          const response = await fetch('/api/revisions/sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ revision }),
          });

          if (!response.ok) {
            throw new Error(`Sync failed: ${response.statusText}`);
          }

          const result = await response.json();

          // Update sync status
          const syncStatus: SyncStatus = {
            isSynced: true,
            lastSyncAttempt: new Date().toISOString(),
            cloudRevisionId: result.cloudRevisionId,
          };

          await storage.updateSyncStatus(revisionId, syncStatus);

          return { data: undefined };
        } catch (error) {
          // Update sync status with error
          const manager = getRevisionManager();
          const storage = (manager as any).storage;

          const syncStatus: SyncStatus = {
            isSynced: false,
            lastSyncAttempt: new Date().toISOString(),
            syncError: String(error),
            retryCount: 1,
          };

          await storage.updateSyncStatus(revisionId, syncStatus);

          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { revisionId }) => [
        { type: 'Revision', id: revisionId },
        'RevisionStats',
      ],
    }),

    // Batch sync pending revisions
    syncPendingRevisions: builder.mutation<
      { synced: number; failed: number },
      { authToken: string }
    >({
      queryFn: async ({ authToken }) => {
        try {
          const manager = getRevisionManager();
          const storage = (manager as any).storage;
          const pendingRevisions = await storage.getPendingSyncRevisions();

          let synced = 0;
          let failed = 0;

          // Batch sync with rate limiting
          const batchSize = 10;
          for (let i = 0; i < pendingRevisions.length; i += batchSize) {
            const batch = pendingRevisions.slice(i, i + batchSize);

            const response = await fetch('/api/revisions/sync-batch', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({ revisions: batch }),
            });

            if (response.ok) {
              const result = await response.json();
              synced += result.synced;
              failed += result.failed;

              // Update sync status for successful syncs
              for (const revisionId of result.syncedIds) {
                await storage.updateSyncStatus(revisionId, {
                  isSynced: true,
                  lastSyncAttempt: new Date().toISOString(),
                });
              }
            } else {
              failed += batch.length;
            }

            // Rate limiting delay
            await new Promise((resolve) => setTimeout(resolve, 100));
          }

          return { data: { synced, failed } };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            },
          };
        }
      },
      invalidatesTags: ['RevisionHistory', 'RevisionStats'],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useCreateRevisionMutation,
  useGetRevisionHistoryQuery,
  useGetRevisionQuery,
  useGenerateDiffQuery,
  useGetRevisionStatsQuery,
  useGetRevisionSettingsQuery,
  useUpdateRevisionSettingsMutation,
  useExportRevisionsQuery,
  useImportRevisionsMutation,
  useSyncRevisionToCloudMutation,
  useSyncPendingRevisionsMutation,
} = revisionApi;
